/* eslint-disable @typescript-eslint/naming-convention */

/* eslint-disable typescript-enum/no-enum */
export type BffErrorCode =
  | BffAppError
  | BffJsonError
  | BffUtf8Error
  | BffNetworkError
  | BffMssLookupServiceError
  | BffPingError
  | BffSharedApiError
  | BffContentfulError
  | BffJwtError
  | BffSwiftypeError
  | BffMdsApiError
  | BffBruceError
  | BffQasError
  | BffTwilioError
  | BffDonnaApiError
  | BffEncryptionError
  | BffCanadaPostError
  | BffFeatureFlagError
  | BffDmsError
  | BffOracleReportsServer
  | BffAnnouncementError
  | BffNoticeContainerError
  | BffBingCustomSearchError;

/**
 * The standard BFF (Backend for Frontend) error format. This should be used as
 * the `E` variant of `Result<T, E>` with in the app
 */
export interface BffError {
  /** Which Bff error code is associated with this error? */
  code: BffErrorCode;
  /** Why did this error occur? */
  cause: string;
  /** What function/module triggered this error? */
  moduleName: string;
}

/**
 * BffUserError represents 4xx HTTP errors (user errors).
 * If you receive this error in an API response, there was _no_ problem with the system.
 * The API response should contain information about what was wrong with the request
 */
export enum BffUserError {
  UserError = 'E0001',
  InvalidLoginCredentials = 'E0002',
  AccountLocked = 'E0003',
  InvalidOtp = 'E0005',
  NoMfaDevicesAvailable = 'E0006',
  MissingPreLoginJwt = 'E0007',
  MissingAccessToken = 'E0008',
  NoMatchesFound = 'E0009',
  MultipleUserInfoEntries = 'E0010',
  NonUniqueContactInfo = 'E0011',
  PasswordHasBeenUsedPreviously = 'E0012',
  ProvidedIncorrectPassword = 'E0013',
  UnknownContactInfoId = 'E0014',
  IncompatibleOTPVerificationChannel = 'E0015',
  ContactInfoIdIncompatibleWithOTPVerification = 'E0016',
  BrokenAccount = 'E0017',
  UserAlreadyRegistered = 'E0018',
  RegistrationAttemptsExceeded = 'E0019',
  IneligibleForRegistration = 'E0020',
  UnknownSid = 'E0021',
  TooManyRequests = 'E0022',
  UsernameUnavailableForRegistration = 'E0023',
  DuplicateEmail = 'E0024',
  AccountDisabled = 'E0025',
  InactiveAccessToken = 'E0026',
  AllMembershipsIneligible = 'E0027',
  EmailIneligibleForLogin = 'E0028',
  TemporarilyIneligibleForLogin = 'E0029',
  UnauthorizedToViewReport = 'E0030',
  NoIncomeTaxSlipsToGenerate = 'E0031',
  NotAllowedToUpdateSpousal = 'E0032',
  ProvidedPartyIdDoesNotMatchAnySpouse = 'E0033',
  MustEndCurrentSpouseRelationship = 'E0034',
  MarriageStartMustNotBePriorToPreviousMarriageEnd = 'E0035'
}

/**
 * System errors contain all known system errors. Ideally, every possible error
 * that the BFF can encounter should be encoded in the enums below. This helps
 * an enormous amount with debugging production issues and documenting various
 * fail-states in the app
 */
export enum BffAppError {
  Unknown = 'E1001',
  MissingEnvironmentVariables = 'E1002',
  NoMembershipRole = 'E1003'
}

export enum BffUtf8Error {
  TextParseError = 'E2000'
}

export enum BffJsonError {
  JsonParseError = 'E3001',
  JsonStringifyError = 'E3002'
}

export enum BffNetworkError {
  HTTPError = 'E4001',
  BackOffFailed = 'E4002'
}

export enum BffMssLookupServiceError {
  ReceivedErrorResponse = 'E5001',
  ReceivedIncorrectResponseShape = 'E5002'
}

export enum BffPingError {
  ReceivedErrorResponse = 'E8001',
  ReceivedIncorrectResponseShape = 'E8002',
  NoPfCookieSet = 'E8003',
  CompletedEarly = 'E8004',
  StateMismatch = 'E8005',
  InvalidAction = 'E8006',
  NoPartyNumberAvailable = 'E8007',
  NoUsernameAvailable = 'E8008',
  InvalidSessionID = 'E8009',
  SessionIdNotFound = 'E8010',
  NoValidSessions = 'E8011',
  UnactivatedAccount = 'E8012'
}

export enum BffSharedApiError {
  AuthenticationFailed = 'E9001',
  InvalidEndpoint = 'E9002',
  ReceivedErrorResponse = 'E9003',
  ReceivedIncorrectResponseShape = 'E9004',
  FoundInPingButNotFoundInMss = 'E9005',
  NotFoundInPing = 'E9006'
}

export enum BffContentfulError {
  ReceivedErrorResponse = 'E11001',
  ReceivedIncorrectResponseShape = 'E11002',
  InvalidNavigationConfig = 'E11003',
  SdkError = 'E11004'
}

export enum BffJwtError {
  UnableToGenerateJwt = 'E12001',
  InvalidJwt = 'E12002'
}

export enum BffSwiftypeError {
  ReceivedErrorResponse = 'E13001',
  ReceivedIncorrectResponseShape = 'E13002',
  EmptyResponseBody = 'E13003'
}

export enum BffBingCustomSearchError {
  ReceivedErrorResponse = 'E13004',
  ReceivedIncorrectResponseShape = 'E13005',
  EmptyResponseBody = 'E13006'
}

export enum BffMdsApiError {
  ReceivedErrorResponse = 'E14001',
  ReceivedIncorrectResponseShape = 'E14002',
  NoDataFound = 'E14003',
  ReceivedUnauthorizedResponse = 'E14004',
  ReceivedNotFoundResponse = 'E14005',
  ReceivedBadRequestResponse = 'E14006',
  ReceivedPSSError = 'E14007',
  CannotDeleteError = 'E14008',
  NoPartyNumberError = 'E14009',
  NoBirthDateError = 'E14010'
}

export enum BffBruceError {
  AuthenticationFailed = 'E15001',
  ReceivedIncorrectResponseShape = 'E15002',
  ReceivedInvalidQueryResponse = 'E15003',
  Received401Response = 'E15004',
  ReceivedInvalidResponse = 'E15005',
  AddUsernameFailed = 'E15006',
  CaseCreationFailed = 'E15007',
  NoCaseDetailsFound = 'E15008'
}

export enum BffQasError {
  ReceivedErrorResponse = 'E16001',
  ReceivedIncorrectResponseShape = 'E16002'
}

export enum BffTwilioError {
  ReceivedUnknownErrorResponse = 'E17001',
  ReceivedIncorrectResponseShape = 'E17002',
  AuthenticationFailed = 'E17003',
  InvalidRecipient = 'E17004',
  FailedToInstantiateClient = 'E17005',
  SmsNotSupportedByLandline = 'E17006'
}

export enum BffDonnaApiError {
  AuthenticationFailed = 'E18001',
  ReceivedErrorResponse = 'E18002',
  ReceivedIncorrectResponseShape = 'E18003',
  EmailAddressNotFound = 'E18004',
  PhoneNumberNotFound = 'E18005',
  getJourneyDataListFailed = 'E18006',
  GetJourneyDetailsFailed = 'E18007',
  GetExternalPartyIdFailed = 'E18008',
  InvalidExternalPartyId = 'E18009',
  getRTAPensionPlansFailed = 'E18010'
}

export enum BffEncryptionError {
  UnableToEncrypt = 'E20001',
  UnableToDecrypt = 'E20002'
}

export enum BffCanadaPostError {
  ReceivedErrorResponse = 'E21001',
  ReceivedIncorrectResponseShape = 'E22002'
}

export enum BffFeatureFlagError {
  InvalidFeatureFlagShape = 'E23001',
  MissingFeatureFlagEntry = 'E23002'
}

export enum BffDmsError {
  FileSizeExceeded = 'E24006',
  FileFormatNotSupported = 'E24007',
  EncryptedPdf = 'E24008',
  FileUploadFailed = 'E24009',
  FileDownloadFailed = 'E24010',
  FileMetadataNotFound = 'E24011',
  FileDoesNotBelongToUser = 'E24012',
  ServerErrorFindingFileMetadata = 'E24013',
  ReceivedIncorrectResponseShape = 'E24014',
  ModifyMetadataFailed = 'E24015',
  PartialUploadError = 'E24016'
}

export enum BffOracleReportsServer {
  ReceivedHTML = 'E25001',
  ReceivedIncorrectResponseShape = 'E25002',
  InvalidBinaryData = 'E25003',
  ReportMissing = 'E25004',
  NtlmError = 'E25005'
}

export enum BffAnnouncementError {
  MissingAnnouncementEntry = 'E26001',
  MoreThanOneAnnouncementFound = 'E26002'
}

export enum BffNoticeContainerError {
  MissingNoticeContainerEntry = 'E27001',
  MoreThanOneNoticeContainerFound = 'E27002'
}

/**
 * Information about each of the BFF error codes that can be used for responding
 * to requests, logging and debugging
 */
interface BffErrorInfoItem {
  /** Message that will get sent in the API response back to the client. Message should be vague enough that a crafty end user can't use it to glean information about the internals of our architecture */
  responseText: string;
  /** Message that will be logged in GCP */
  logText: string;
}

const errorInfo: Record<BffErrorCode, BffErrorInfoItem> = {
  // Application Errors
  [BffAppError.Unknown]: {
    responseText: 'An unknown error occurred',
    logText: 'An unknown error occurred'
  },

  [BffAppError.MissingEnvironmentVariables]: {
    responseText: 'Something went wrong on our end',
    logText: 'The application is missing one or more environment variables'
  },

  [BffAppError.NoMembershipRole]: {
    responseText: 'No MembershipRole found',
    logText: 'No MembershipRole found'
  },

  // UTF-8 Errors
  [BffUtf8Error.TextParseError]: {
    responseText: 'Something went wrong on our end',
    logText: 'Unable to parse text as UTF-8'
  },

  // JSON errors
  [BffJsonError.JsonParseError]: {
    responseText: 'Something went wrong on our end',
    logText: 'Unable to parse JSON'
  },
  [BffJsonError.JsonStringifyError]: {
    responseText: 'Something went wrong on our end',
    logText: 'Unable to stringify JSON'
  },

  // Network Errors
  [BffNetworkError.HTTPError]: {
    responseText: 'Unable to connect to an external service',
    logText:
      'While the BFF was making an outbound request, a network failure occurred, or something prevented the request from completing'
  },
  [BffNetworkError.BackOffFailed]: {
    responseText: 'Repeatedly received a bad response from an external service',
    logText:
      'While the BFF was making an outbound request (repeatedly with backoff), the external service never returned a favorable response.'
  },

  // MSS Lookup Service errors
  [BffMssLookupServiceError.ReceivedErrorResponse]: {
    responseText: 'Failed to authenticate with MSS Lookup Service',
    logText: 'Unable to authenticate MSS Lookup Service'
  },
  [BffMssLookupServiceError.ReceivedIncorrectResponseShape]: {
    responseText: 'Invalid MSS Lookup Service Endpoint',
    logText: 'Unable to find requested endpoint from MSS Lookup Service'
  },

  // Ping Federate errors
  [BffPingError.ReceivedErrorResponse]: {
    responseText: 'Received error response from Ping',
    logText: 'Got a response from Ping, but the response indicated that an error occurred'
  },

  [BffPingError.ReceivedIncorrectResponseShape]: {
    responseText: 'Ping returned bad data',
    logText: "Ping sent back an API response with a body that doesn't match the shape that our codebase expects"
  },

  [BffPingError.NoPfCookieSet]: {
    responseText: 'Invalid response from Ping',
    logText: "Ping didn't attempt to set a 'PF' cookie for us to complete the flow with"
  },

  [BffPingError.CompletedEarly]: {
    responseText: 'Invalid response from Ping',
    logText:
      "Ping responded with 'code: COMPLETED' earlier than expected. This could indicate that we sent a PF cookie with the request when we shouldn't have"
  },

  [BffPingError.StateMismatch]: {
    responseText: 'Invalid response from Ping',
    logText: "Ping responded with a 'state' that doesn't match the one we initially sent for this flow"
  },
  [BffPingError.NoPartyNumberAvailable]: {
    responseText: 'Invalid response from Ping',
    logText: 'Did not receive party ID from Ping'
  },
  [BffPingError.NoUsernameAvailable]: {
    responseText: 'Invalid response from Ping',
    logText: 'Did not receive username from Ping'
  },
  [BffPingError.InvalidAction]: {
    responseText: 'Invalid Ping authentication flow action attempted',
    logText:
      "The id of the attempted action was not listed in the list of allowed followup actions from Ping's previous response"
  },
  [BffPingError.InvalidSessionID]: {
    responseText: 'Invalid Ping session ID provided to revoke',
    logText: 'Unable to revoke the session ID due to an invalid session ID'
  },
  [BffPingError.SessionIdNotFound]: {
    responseText: 'Ping session ID not found',
    logText: 'Cound not find the session ID through the Ping Access Token'
  },
  [BffPingError.NoValidSessions]: {
    responseText: 'No valid sessions found',
    logText: 'No valid sessions found for the given sri'
  },
  [BffPingError.UnactivatedAccount]: {
    responseText: 'UnactivatedAccount',
    logText: 'UnactivatedAccount received from Ping'
  },
  // Ping Shared API errors
  [BffSharedApiError.AuthenticationFailed]: {
    responseText: 'Failed to authenticate with Ping Shared API',
    logText: 'Unable to authenticate Ping SharedApi'
  },
  [BffSharedApiError.InvalidEndpoint]: {
    responseText: 'Invalid Shared Api Endpoint',
    logText: 'Unable to find requested endpoint from shared api'
  },
  [BffSharedApiError.ReceivedErrorResponse]: {
    responseText: 'Received error response from SharedApi',
    logText: 'SharedApi returned a response with status: "FAILED"'
  },
  [BffSharedApiError.ReceivedIncorrectResponseShape]: {
    responseText: 'SharedApi returned bad data',
    logText: "SharedApi sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffSharedApiError.FoundInPingButNotFoundInMss]: {
    responseText: 'Received NOT_FOUND error from SharedApi',
    logText: 'SharedApi returned an error with code: "NOT_FOUND" and message: "Cannot find userid"'
  },
  [BffSharedApiError.NotFoundInPing]: {
    responseText: 'Received NOT_FOUND error from SharedApi',
    logText: 'SharedApi returned an error with code: "NOT_FOUND" and message: "Cannot find user with partyNumber"'
  },
  [BffContentfulError.ReceivedErrorResponse]: {
    responseText: 'Failed to make api call to Contentful',
    logText: 'Contentful returned an error response'
  },
  [BffContentfulError.ReceivedIncorrectResponseShape]: {
    responseText: 'Contentful returned bad data',
    logText: "Contentful sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffContentfulError.InvalidNavigationConfig]: {
    responseText: 'Invalid Contentful navigation config',
    logText: "Contentful sent back a navigation config that doesn't match the shape that our codebase expects"
  },
  [BffContentfulError.SdkError]: {
    responseText: 'Contentful SDK error',
    logText: 'An error was encountered while using the contentful npm package.'
  },
  [BffJwtError.UnableToGenerateJwt]: {
    responseText: 'Failed to generate JWT token',
    logText: 'jwtwebtoken library returned an error while attempting to generate JWT token'
  },
  [BffJwtError.InvalidJwt]: {
    responseText: 'Failed to verify JWT token',
    logText: 'jsonwebtoken.verify was unable to validate an access token'
  },
  [BffSwiftypeError.ReceivedErrorResponse]: {
    responseText: 'Swiftype returned a response with an error message',
    logText: 'Swiftype returned a response with an error message"'
  },
  [BffSwiftypeError.ReceivedIncorrectResponseShape]: {
    responseText: 'Swiftype returned bad data',
    logText: "Swiftype sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffSwiftypeError.EmptyResponseBody]: {
    responseText: 'Swiftype returned an empty response',
    logText:
      "Swiftype sent back a response with an empty body. This doesn't have a definite cause, but it could mean that the engine key is invalid."
  },
  [BffBingCustomSearchError.ReceivedErrorResponse]: {
    responseText: 'Bing returned a response with an error message',
    logText: 'Bing returned a response with an error message"'
  },
  [BffBingCustomSearchError.ReceivedIncorrectResponseShape]: {
    responseText: 'Bing returned bad data',
    logText: "Bing sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffBingCustomSearchError.EmptyResponseBody]: {
    responseText: 'Bing returned an empty response',
    logText:
      "Bing sent back a response with an empty body. This doesn't have a definite cause, but it could mean that the engine key is invalid."
  },
  [BffMdsApiError.ReceivedErrorResponse]: {
    responseText: 'Received error response from MDS API',
    logText: 'MDS API returned a response with an error message"'
  },
  [BffMdsApiError.ReceivedIncorrectResponseShape]: {
    responseText: 'MDS API returned bad data',
    logText: "MDS API sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffMdsApiError.NoDataFound]: {
    responseText: 'MDS API returned null',
    logText:
      'MDS API returned null. This may mean that the user cannot be found in mss or pss. However, it can also occur in situations where the endpoint is not applicable to the member. For example, trying to get the pension status for an active member'
  },
  [BffMdsApiError.ReceivedUnauthorizedResponse]: {
    responseText: 'Invalid Access Token',
    logText: 'MDS Donna API Gateway was unable to validate the access token'
  },
  [BffMdsApiError.ReceivedNotFoundResponse]: {
    responseText: 'Route Not Found',
    logText: 'MDS was not able to find the requested route'
  },
  [BffMdsApiError.ReceivedBadRequestResponse]: {
    responseText: 'Bad Request',
    logText: 'MDS received a bad request. Check the data sent to MDS to ensure it matches the expected format'
  },
  [BffMdsApiError.ReceivedPSSError]: {
    responseText: 'MDS received an error upstream',
    logText: 'MDS received an error from PSS'
  },
  [BffMdsApiError.CannotDeleteError]: {
    responseText: 'Cannot delete user',
    logText: 'MDS was unable to delete the user from the m_user table'
  },
  [BffMdsApiError.NoPartyNumberError]: {
    responseText: 'No party number found',
    logText: 'MDS was unable to find the party number for the given username/email. This should never happen.'
  },
  [BffMdsApiError.NoBirthDateError]: {
    responseText: 'No birth date found',
    logText: 'MDS was unable to find the birth date associated with the membership. This should never happen.'
  },
  [BffBruceError.AuthenticationFailed]: {
    responseText: 'Failed to authenticate with Bruce',
    logText: 'Unable to get an access token from the Bruce authorization service'
  },
  [BffBruceError.ReceivedIncorrectResponseShape]: {
    responseText: 'Bruce returned bad data',
    logText: "Bruce sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffBruceError.ReceivedInvalidQueryResponse]: {
    responseText: 'Bruce returned a failure response',
    logText: 'Bruce sent back a response with an errorCode'
  },
  [BffBruceError.Received401Response]: {
    responseText: 'Bruce returned a 401 unauthorized',
    logText: 'Bruce sent back a 401 response because the access token was invalid or expired'
  },
  [BffBruceError.ReceivedInvalidResponse]: {
    responseText: 'Bruce returned a status 500 Internal Server Error',
    logText: 'Bruce sent an Internal Server Error response'
  },
  [BffBruceError.AddUsernameFailed]: {
    responseText: 'Failed to add username to Bruce',
    logText: 'Unable to associate username to party number in Bruce'
  },
  [BffBruceError.CaseCreationFailed]: {
    responseText: 'Failed to create secure comm case in Bruce',
    logText: 'Unable to create secure comm case in Bruce'
  },
  [BffBruceError.NoCaseDetailsFound]: {
    responseText: 'No case details found in Bruce',
    logText: 'No case details found in Bruce'
  },
  [BffQasError.ReceivedErrorResponse]: {
    responseText: 'QAS returned an error response',
    logText: 'QAS sent back an error response'
  },
  [BffQasError.ReceivedIncorrectResponseShape]: {
    responseText: 'QAS returned bad data',
    logText: "QAS sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffTwilioError.ReceivedUnknownErrorResponse]: {
    responseText: 'Twilio service returned an error response',
    logText:
      'Twilio service returned an error response that we do not handle. Look at the error code from Twilio and consider if it needs to be handled as a BffError.'
  },
  [BffTwilioError.ReceivedIncorrectResponseShape]: {
    responseText: 'Twilio service returned bad data',
    logText: "Twilio service sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffTwilioError.AuthenticationFailed]: {
    responseText: 'Failed to authenticate with Twilio',
    logText: 'Check the Twilio credentials in the environment variables'
  },
  [BffTwilioError.InvalidRecipient]: {
    responseText: 'Invalid recipient',
    logText:
      'Twilio rejected the `to` parameter as an invalid recipient. Check if the recipient is a valid phone number or email address. Or if it is being sent to the wrong channel.'
  },
  [BffTwilioError.FailedToInstantiateClient]: {
    responseText: 'Failed to initialize OTP provider',
    logText: 'While trying to instantiate the Twilio client, the `twilio()` function threw'
  },
  [BffTwilioError.SmsNotSupportedByLandline]: {
    responseText: 'SMS is not supported by landline phone number',
    logText:
      'While trying to instantiate the Twilio client, the `twilio()` function threw SMS is not supported by landline phone number'
  },
  [BffDonnaApiError.AuthenticationFailed]: {
    responseText: 'Failed to authenticate with the Donna API',
    logText: 'Unable to get an access token from the Donna authorization service'
  },
  [BffDonnaApiError.ReceivedErrorResponse]: {
    responseText: 'Received error response from Donna',
    logText: 'Donna API returned a response with an error message"'
  },
  [BffDonnaApiError.ReceivedIncorrectResponseShape]: {
    responseText: 'Donna returned bad data',
    logText: "Donna API sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffDonnaApiError.EmailAddressNotFound]: {
    responseText: 'Email address not found',
    logText: 'Could not find email address'
  },
  [BffDonnaApiError.PhoneNumberNotFound]: {
    responseText: 'Phone number not found',
    logText: 'Could not find phone number'
  },
  [BffDonnaApiError.getJourneyDataListFailed]: {
    responseText: 'Failed to get journey data list',
    logText: 'Failed to get journey data list'
  },
  [BffDonnaApiError.GetJourneyDetailsFailed]: {
    responseText: 'Failed to get journey details',
    logText: 'Failed to get journey details'
  },
  [BffDonnaApiError.GetExternalPartyIdFailed]: {
    responseText: 'Failed to get external party id',
    logText: 'Failed to get external party id'
  },
  [BffDonnaApiError.InvalidExternalPartyId]: {
    responseText: 'Invalid external party id',
    logText: 'Invalid external party id'
  },
  [BffDonnaApiError.getRTAPensionPlansFailed]: {
    responseText: 'Failed to get RTA pension plans',
    logText: 'Failed to get RTA pension plans'
  },
  [BffEncryptionError.UnableToEncrypt]: {
    responseText: 'Something went wrong on our end',
    logText: 'Unable to encrypt data'
  },
  [BffEncryptionError.UnableToDecrypt]: {
    responseText: 'Something went wrong on our end',
    logText: 'Unable to decrypt data'
  },
  [BffCanadaPostError.ReceivedErrorResponse]: {
    responseText: 'CanadaPost returned an error response',
    logText: 'CanadaPost sent back an error response'
  },
  [BffCanadaPostError.ReceivedIncorrectResponseShape]: {
    responseText: 'CanadaPost returned bad data',
    logText: "CanadaPost sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffFeatureFlagError.InvalidFeatureFlagShape]: {
    responseText: 'Invalid feature flag shape',
    logText: 'The feature flag data does not match the expected shape'
  },
  [BffFeatureFlagError.MissingFeatureFlagEntry]: {
    responseText: 'Missing feature flag entry',
    logText: 'No feature flags found for the current environment'
  },
  [BffDmsError.FileSizeExceeded]: {
    responseText: 'File size exceeded',
    logText: 'The file size exceeded the maximum size allowed by Donna'
  },
  [BffDmsError.FileFormatNotSupported]: {
    responseText: 'File format not supported',
    logText: 'The file format is not supported by Donna'
  },
  [BffDmsError.EncryptedPdf]: {
    responseText: 'Encrypted PDF',
    logText: 'The PDF file is encrypted'
  },
  [BffDmsError.FileUploadFailed]: {
    responseText: 'File upload failed',
    logText: 'The file upload failed'
  },
  [BffDmsError.FileDownloadFailed]: {
    responseText: 'File download failed',
    logText: 'The file download failed'
  },
  [BffDmsError.FileMetadataNotFound]: {
    responseText: 'File metadata not found',
    logText: 'The file metadata was not found'
  },
  [BffDmsError.FileDoesNotBelongToUser]: {
    responseText: 'File does not belong to user',
    logText: 'The file does not belong to the user'
  },
  [BffDmsError.ServerErrorFindingFileMetadata]: {
    responseText: 'Server error finding file metadata',
    logText: 'There was a server error while finding the file metadata'
  },
  [BffDmsError.ReceivedIncorrectResponseShape]: {
    responseText: 'Received incorrect response shape',
    logText: 'Received an incorrect response shape'
  },
  [BffDmsError.ModifyMetadataFailed]: {
    responseText: 'Modify metadata failed',
    logText: 'Failed to modify metadata'
  },
  [BffOracleReportsServer.ReceivedHTML]: {
    responseText: 'Received HTML from the reports server instead of a PDF report',
    logText: 'Received HTML from the reports server instead of a PDF report. This is likely an error page.'
  },
  [BffOracleReportsServer.ReceivedIncorrectResponseShape]: {
    responseText: 'The reports server returned bad data',
    logText:
      "The Oracle Reports Server sent back a response with a body that doesn't match the shape that our codebase expects"
  },
  [BffOracleReportsServer.InvalidBinaryData]: {
    responseText: 'Invalid PDF data',
    logText: 'The Oracle Reports Server returned binary data that does not appear to be a valid PDF file.'
  },
  [BffOracleReportsServer.ReportMissing]: {
    responseText: 'Report was expected to exist on the reports server, but it is missing.',
    logText:
      'Report was expected to exist on the reports server, but it is missing. This is usually because of stale data after a data migration, and does not happen in production.'
  },
  [BffOracleReportsServer.NtlmError]: {
    responseText: 'Something went wrong on our end',
    logText: 'The "httpntlm" package threw an error'
  },
  [BffDmsError.PartialUploadError]: {
    responseText: 'Partial upload error',
    logText: 'Partial upload error'
  },
  [BffAnnouncementError.MissingAnnouncementEntry]: {
    responseText: 'Missing Announcements Container entry',
    logText: 'No Announcements Container found'
  },
  [BffAnnouncementError.MoreThanOneAnnouncementFound]: {
    responseText: 'More than one Announcements Container entry found',
    logText: 'More than one Announcements Container entry found'
  },
  [BffNoticeContainerError.MissingNoticeContainerEntry]: {
    responseText: 'Missing Notice Container entry',
    logText: 'No Notice Container found'
  },
  [BffNoticeContainerError.MoreThanOneNoticeContainerFound]: {
    responseText: 'More than one Notice Container entry found',
    logText: 'More than one Notice Container entry found'
  }
};

/**
 * Gets the error information associated with a given error code
 */
export const getErrorCodeInfo = (errorCode: BffErrorCode): BffErrorInfoItem => errorInfo[errorCode];
